import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { AttributeConflict } from '../../utils/attributeConflictDetector';

/**
 * State for the polygon merge tool.
 *
 * Tracks whether the merge tool is currently active and manages the attribute
 * resolution dialog state when merged features have conflicting attribute values.
 *
 * **Validates: Requirement 15.1** – WHEN a Registered_User selects two or more
 * adjacent polygon features and activates merge tool, THE Vector_Editor SHALL
 * combine the polygons into a single feature.
 *
 * **Validates: Requirement 15.3** – WHEN merged features have different attribute
 * values, THE Vector_Editor SHALL prompt the user to select which attributes to keep.
 */
export interface MergeToolState {
  /** Whether the merge tool is currently active */
  isMergeToolActive: boolean;
  /** Whether the attribute resolution dialog is open */
  showAttributeResolutionDialog: boolean;
  /** Conflicting attributes detected between features being merged */
  conflictingAttributes: AttributeConflict[];
  /** User's resolved attribute choices */
  resolvedAttributes: Record<string, unknown>;
}

const initialState: MergeToolState = {
  isMergeToolActive: false,
  showAttributeResolutionDialog: false,
  conflictingAttributes: [],
  resolvedAttributes: {},
};

const mergeToolSlice = createSlice({
  name: 'mergeTool',
  initialState,
  reducers: {
    /**
     * Activate the merge tool. Requires 2+ polygon features to be selected
     * before the merge operation can proceed.
     */
    activateMergeTool: (state) => {
      state.isMergeToolActive = true;
    },

    /**
     * Deactivate the merge tool and return to normal interaction.
     */
    deactivateMergeTool: (state) => {
      state.isMergeToolActive = false;
      state.showAttributeResolutionDialog = false;
      state.conflictingAttributes = [];
      state.resolvedAttributes = {};
    },

    /**
     * Toggle the merge tool on/off.
     */
    toggleMergeTool: (state) => {
      state.isMergeToolActive = !state.isMergeToolActive;
    },

    /**
     * Open the attribute resolution dialog with detected conflicts.
     */
    openAttributeResolutionDialog: (state, action: PayloadAction<AttributeConflict[]>) => {
      state.showAttributeResolutionDialog = true;
      state.conflictingAttributes = action.payload;
      state.resolvedAttributes = {};
    },

    /**
     * Close the attribute resolution dialog without resolving.
     */
    closeAttributeResolutionDialog: (state) => {
      state.showAttributeResolutionDialog = false;
      state.conflictingAttributes = [];
      state.resolvedAttributes = {};
    },

    /**
     * Set the resolved value for a single attribute field.
     */
    setResolvedAttribute: (state, action: PayloadAction<{ field: string; value: unknown }>) => {
      state.resolvedAttributes[action.payload.field] = action.payload.value;
    },

    /**
     * Set all resolved attributes at once (e.g., "Keep First" or "Keep Last" strategy).
     */
    setAllResolvedAttributes: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.resolvedAttributes = action.payload;
    },

    /**
     * Confirm the attribute resolution and close the dialog.
     * The resolved attributes remain in state for the thunk to consume.
     */
    confirmAttributeResolution: (state) => {
      state.showAttributeResolutionDialog = false;
    },
  },
});

export const {
  activateMergeTool,
  deactivateMergeTool,
  toggleMergeTool,
  openAttributeResolutionDialog,
  closeAttributeResolutionDialog,
  setResolvedAttribute,
  setAllResolvedAttributes,
  confirmAttributeResolution,
} = mergeToolSlice.actions;

export default mergeToolSlice.reducer;

/* -------------------------------------------------------------------------- */
/*  Selectors                                                                 */
/* -------------------------------------------------------------------------- */

/** Raw slice state. */
const selectMergeToolState = (state: RootState): MergeToolState => state.mergeTool;

/** Whether the merge tool is currently active. */
export const selectIsMergeToolActive = createSelector(
  selectMergeToolState,
  (mergeTool) => mergeTool.isMergeToolActive
);

/** Whether the attribute resolution dialog is open. */
export const selectShowAttributeResolutionDialog = createSelector(
  selectMergeToolState,
  (mergeTool) => mergeTool.showAttributeResolutionDialog
);

/** The conflicting attributes to display in the dialog. */
export const selectConflictingAttributes = createSelector(
  selectMergeToolState,
  (mergeTool) => mergeTool.conflictingAttributes
);

/** The user's resolved attribute choices. */
export const selectResolvedAttributes = createSelector(
  selectMergeToolState,
  (mergeTool) => mergeTool.resolvedAttributes
);
